import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Button from "../elements/button";
import Icon from "../elements/icon.js";
import useFocus from "../../hooks/use-focus";

const TextInputForm = ({
  label,
  placeholder = "",
  value = "",
  onSubmit = () => {},
  isSubmitting = false,
  textClass = "",
  required = false,
  disableEdit = false,
  onCancel = () => {},
  ...rest
}) => {
  const [inputRef, setInputFocus] = useFocus();
  const [isEditMode, setIsEditMode] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const isDisabled = (required && !localValue) || isSubmitting;

  useEffect(() => {
    if (isEditMode) {
      setInputFocus();
    }
  }, [isEditMode, setInputFocus]);

  useEffect(() => {
    if (disableEdit) {
      setIsEditMode(false);
      setLocalValue(value);
    }
  }, [disableEdit, value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(localValue);
    if (localValue.trim().length !== 0) {
      setIsEditMode(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setIsEditMode(false);
    setLocalValue(value);
  };

  const enableEditMode = () => {
    setLocalValue(value);
    setIsEditMode(true);
  };

  if (isEditMode || isSubmitting) {
    return (
      <form className="et--text-input-form--input-wrapper" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          aria-label={label}
          className="et--text-input"
          type="text"
          onChange={(e) => setLocalValue(e.target.value)}
          value={localValue}
          placeholder={placeholder}
          required={required}
          disabled={isSubmitting}
          {...rest}
        />
        <Button className="et--text-input-form--save-button" type="simple" onClick={handleSubmit} disabled={isDisabled}>
          Save
        </Button>
        <Button
          className="et--text-input-form--cancel-button"
          type="simple"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </form>
    );
  }

  return (
    <Button
      className="et--text-input-form--edit-button"
      type="simple"
      label={`Edit ${label}`}
      onClick={enableEditMode}
      disabled={disableEdit}
    >
      <div className={classNames("et--text-input-form--text", textClass)}>{value || placeholder}</div>
      <Icon icon="edit" />
    </Button>
  );
};

TextInputForm.propTypes = {
  /**
   * aria-label applied to the penci edit icon with "Edit" appended as well as the input field in edit mode
   */
  label: PropTypes.string.isRequired,
  /**
   * placeholder text to display when no value has been set in both read and edit modes
   */
  placeholder: PropTypes.string,
  /**
   * The value to be displayed in both read and edit modes
   */
  value: PropTypes.string,
  /**
   * Callback function typically used to make api call and update value passed in as prop
   */
  onSubmit: PropTypes.func,
  /**
   * Boolean indicating that form is submitting and form should be disabled
   */
  isSubmitting: PropTypes.bool,
  /**
   * Css class applied to the value in read mode
   */
  textClass: PropTypes.string,
  /**
   * Indicates if a value is required or can be blank
   */
  required: PropTypes.bool,
  /**
   * Disable the edit pencil so form cannot be opened
   */
  disableEdit: PropTypes.bool,
  /**
   * Callback function used when process is canceled
   */
  onCancel: PropTypes.func,
};

export default TextInputForm;
